import { observer } from 'mobx-react-lite';
import { ClientItemId } from 'owa-client-ids';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import * as React from 'react';
import * as lazyTriageActions from 'owa-mail-triage-action';
import Message from 'owa-service/lib/contract/Message';
import {
    Reaction_SetAnnouncement_Text,
    Reaction_UnsetAnnouncement_Text,
} from '../utils/reactions.locstring.json';
import loc, { format } from 'owa-localize';
import { ReactionsMap, getReactionResourceData } from 'owa-mail-reactions-data';
import { LottieAnimation, LottieAnimationRef } from 'owa-lottie-animation';

const styles = require('./Reactions.scss');

export interface AddReactionButtonProps {
    reactionType: string;
    conversationId: ClientItemId;
    itemId: ClientItemId;
    instrumentationContext: InstrumentationContext;
    message: Message;
    ownerCurrentReactionType: string;
    onAddReactionClicked: () => void;
}

export const AddReactionButton = observer(function AddReactionButton(
    props: AddReactionButtonProps
) {
    const anim = React.useRef<LottieAnimationRef | null>(null);
    const onPlay = React.useCallback(() => anim.current?.play(), [anim]);
    const onStop = React.useCallback(() => anim.current?.stop(), [anim]);

    const onReaction = (event?: React.MouseEvent<{}>) => {
        const announcementString = format(
            props.ownerCurrentReactionType === props.reactionType
                ? loc(Reaction_UnsetAnnouncement_Text)
                : loc(Reaction_SetAnnouncement_Text),
            ReactionsMap[props.reactionType].localizedName
        );

        lazyTriageActions.lazyReactToItem.importAndExecute(
            props.message,
            props.itemId,
            props.conversationId,
            props.reactionType,
            props.instrumentationContext,
            announcementString,
            props.ownerCurrentReactionType
        );

        props.onAddReactionClicked();
    };

    // If the reaction type is not on the map, show nothing
    if (!ReactionsMap[props.reactionType]) {
        return null;
    }

    const resourceData = getReactionResourceData(props.reactionType, true /* isAnimated */);
    const reactionName = ReactionsMap[props.reactionType].localizedName;
    return (
        <>
            {resourceData && (
                <LottieAnimation
                    ref={anim}
                    handlers={{
                        onClick: onReaction,
                        onMouseEnter: onPlay,
                        onMouseLeave: onStop,
                    }}
                    className={styles.reactionIcon}
                    title={reactionName}
                    animationData={resourceData}
                    autoplay={false}
                />
            )}
            {!resourceData && (
                <div
                    aria-label={reactionName}
                    title={reactionName}
                    className={styles.reactionIcon}
                    onClick={onReaction}>
                    {ReactionsMap[props.reactionType].icon}
                </div>
            )}
        </>
    );
});
